import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Play, Sparkles, Loader2, Gamepad2, ShieldCheck, Film, Heart, UserPlus, UserCheck, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public pre-login landing for a shared creator link (/c/:id) — rich preview + Follow / Support (tip) / Sign-up CTAs.
export default function CreatorLanding() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [tipBusy, setTipBusy] = useState(false);

  const loadCreator = () => api.get(`/creators/${id}`).then(({ data }) => {
    setC(data); setFollowing(!!data?.is_following); setFollowers(data?.followers || 0); setLoading(false);
  }).catch(() => { setC(null); setLoading(false); });

  useEffect(() => { loadCreator(); /* eslint-disable-next-line */ }, [id]);

  // Finalise a returning tip checkout (?tip_session=...).
  useEffect(() => {
    const s = searchParams.get("tip_session");
    if (!s || !user) return;
    api.get(`/tips/status/${s}`).then(({ data }) => {
      if (data.payment_status === "paid") { toast.success("🎉 Thank you for supporting this creator!"); loadCreator(); }
    }).catch(() => {});
    /* eslint-disable-next-line */
  }, [user]);

  const toggleFollow = async () => {
    if (!user) { navigate("/register"); return; }
    setFollowBusy(true);
    try {
      const { data } = following ? await api.delete(`/social/follow/${id}`) : await api.post(`/social/follow/${id}`);
      setFollowing(!!data.following);
      if (typeof data.followers === "number") setFollowers(data.followers);
    } catch { toast.error("Couldn't update follow"); }
    setFollowBusy(false);
  };

  const tip = async (tier) => {
    if (!user) { navigate("/register"); return; }
    setTipBusy(true);
    try {
      const { data } = await api.post("/tips/checkout", { creator_id: id, tier, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start checkout"); }
    setTipBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#05060a] text-white flex flex-col items-center justify-center p-6" data-testid="creator-landing">
      <img src="/brand/gv-wordmark.png" alt="GameView Studio" className="h-9 mb-8 opacity-90" />
      {loading ? (
        <Loader2 className="w-8 h-8 animate-spin text-[#7c5cff]" data-testid="creator-landing-loading" />
      ) : c ? (
        <div className="w-full max-w-xl">
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#11141c] aspect-[1200/630]">
            <img src={`${BACKEND}/api/og/creator/${id}/image.png`} alt={c.name} loading="eager" className="w-full h-full object-cover block" data-testid="creator-landing-image" />
          </div>
          <h1 className="text-3xl font-extrabold mt-6 text-center" data-testid="creator-landing-name">{c.avatar_emoji} {c.name}</h1>
          <p className="text-white/60 text-center mt-1">{c.games_count} games · {c.total_plays} plays{c.clip_stats?.plays > 0 ? ` · ${c.clip_stats.plays} clip plays` : ""} on GameView Studio</p>
          {!c.is_me && (
            <div className="flex items-center justify-center gap-3 mt-3">
              <Button onClick={toggleFollow} disabled={followBusy} data-testid="creator-follow-btn"
                className={`rounded-full ${following ? "bg-white/10 text-white hover:bg-white/15" : "bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]"}`}>
                {followBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {following ? "Following" : "Follow"}
              </Button>
              <span className="text-sm text-white/60" data-testid="creator-followers-count"><b className="text-white">{followers}</b> follower{followers === 1 ? "" : "s"}</span>
            </div>
          )}
          {c.games_count > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3 mt-4" data-testid="creator-trust">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/25">
                <div className="w-9 h-9 rounded-full bg-emerald-500 grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-white" /></div>
                <div className="text-left leading-tight">
                  <div className="text-xl font-extrabold text-emerald-400" data-testid="creator-trust-score">{c.trust_score}<span className="text-sm text-emerald-400/60">/100</span></div>
                  <div className="text-[11px] text-white/50 -mt-0.5">Trust score</div>
                </div>
              </div>
              <span className="text-sm text-white/70" data-testid="creator-verified-count">
                🛡️ <b className="text-white">{c.verified_games}</b> of {c.games_count} verified original
              </span>
            </div>
          )}
          {c.bio && <p className="text-white/70 text-center mt-3 max-w-lg mx-auto">{c.bio}</p>}
          {c.best_clip && (
            <div className="mt-6 rounded-2xl overflow-hidden border border-fuchsia-400/25 bg-fuchsia-500/[0.06]" data-testid="creator-landing-clip">
              <div className="px-3 pt-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-fuchsia-500/25 text-fuchsia-200"><Film className="w-3.5 h-3.5" /> Most-loved gameplay clip</span>
              </div>
              <div className="p-3">
                <video src={`${BACKEND}${c.best_clip.url}`} autoPlay muted loop playsInline controls
                  className="w-full rounded-xl border border-white/10 bg-black block" style={{ aspectRatio: "16/9" }} data-testid="creator-landing-clip-video" />
                <div className="flex items-center justify-between gap-3 mt-2 text-xs text-white/60">
                  <button onClick={() => navigate(`/g/${c.best_clip.game_id}`)} className="font-semibold text-amber-300 hover:underline truncate" data-testid="creator-landing-clip-game">
                    {c.best_clip.cover_emoji || "🎮"} {c.best_clip.game_title}
                  </button>
                  <span className="shrink-0 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {c.best_clip.plays || 0}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {c.best_clip.likes || 0}</span>
                  </span>
                </div>
              </div>
            </div>
          )}
          {c.games?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6" data-testid="creator-landing-games">
              {c.games.slice(0, 6).map((g) => (
                <div key={g.id} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden group">
                  <button onClick={() => navigate(`/g/${g.id}`)} title={g.title} className="w-full text-left">
                    <div className="relative h-20 grid place-items-center overflow-hidden"
                      style={{ background: `linear-gradient(135deg, ${g.world?.bgColor || "#0b1026"}, ${g.world?.accent || "#1f6feb"})` }}>
                      {g.cover_image
                        ? <img src={`${BACKEND}${g.cover_image}`} alt={g.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        : <span className="text-3xl">{g.cover_emoji || "🎮"}</span>}
                      {g.licence?.verified && (
                        <span className="absolute bottom-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-black/60 text-emerald-300 backdrop-blur">
                          <ShieldCheck className="w-2.5 h-2.5" /> Verified
                        </span>
                      )}
                    </div>
                    <div className="px-2.5 pt-2 text-xs font-semibold text-white truncate">{g.title}</div>
                  </button>
                  {g.licence?.verified && g.licence?.certificate_id && (
                    <a href={`/verify/${g.licence.certificate_id}`} target="_blank" rel="noopener noreferrer" data-testid={`creator-verify-${g.id}`}
                      className="block px-2.5 pb-2 pt-0.5 text-[10px] text-white/45 hover:text-emerald-400 transition-colors">
                      Verify certificate ↗
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          {!c.is_me && (
            <div className="mt-7 rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] p-4" data-testid="creator-tip-jar">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-300 mb-1"><Coffee className="w-4 h-4" /> Support {c.name}</div>
              <p className="text-xs text-white/55 mb-3">Send a tip to fuel more games. {c.tips_count > 0 ? `${c.tips_count} tip${c.tips_count === 1 ? "" : "s"} so far 💛` : "Be their first supporter 💛"}</p>
              <div className="grid grid-cols-4 gap-2">
                {[["coffee", "☕", "$1"], ["cheer", "🎉", "$3"], ["fan", "⭐", "$5"], ["super", "🌟", "$10"]].map(([tier, emo, amt]) => (
                  <button key={tier} onClick={() => tip(tier)} disabled={tipBusy} data-testid={`creator-tip-${tier}`}
                    className="rounded-xl border border-white/10 bg-white/[0.04] hover:border-amber-400/50 hover:bg-amber-500/10 py-2.5 flex flex-col items-center gap-0.5 transition-colors disabled:opacity-50">
                    <span className="text-lg">{emo}</span>
                    <span className="text-xs font-bold text-white">{amt}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <Button onClick={() => navigate(`/app/creators/${id}`)} className="rounded-full h-12 px-7 text-base bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="creator-landing-view">
              <Gamepad2 className="w-5 h-5" /> View creator & play
            </Button>
            <Button onClick={() => navigate("/register")} variant="outline" className="rounded-full h-12 px-7 text-base border-white/20 text-white hover:bg-white/10" data-testid="creator-landing-signup">
              <Sparkles className="w-5 h-5" /> Build your own with AI
            </Button>
          </div>
          <p className="text-center text-white/40 text-xs mt-6">Discover AI-built games on GameView Studio — prompt it, play it, publish it.</p>
        </div>
      ) : (
        <div className="text-center max-w-md" data-testid="creator-landing-missing">
          <h1 className="text-2xl font-bold">Creator not found</h1>
          <p className="text-white/60 mt-2">This creator may no longer be available.</p>
          <Button onClick={() => navigate("/")} className="rounded-full mt-5 bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="creator-landing-home">
            <Sparkles className="w-4 h-4" /> Explore GameView Studio
          </Button>
        </div>
      )}
    </div>
  );
}
